import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userApi } from '../lib/api';
import { roleLabels } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, User, Mail, Phone, Shield, RefreshCw, Key, MoreVertical, Send, AlertCircle, Trash2 } from 'lucide-react';
import { authApi } from '../lib/api';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';

const UsersPage = () => {
  const { isAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [showForcePasswordModal, setShowForcePasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [forcePassword, setForcePassword] = useState('');
  
  // Delete user state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const [newUser, setNewUser] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'external_technician'
  });

  const loadUsers = useCallback(async () => {
    try {
      const response = await userApi.getAll();
      setUsers(response.data);
    } catch {
      toast.error('Errore nel caricamento degli utenti');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await authApi.register(newUser);
      if (response.data.email_sent) {
        toast.success('Utente creato! Email di attivazione inviata.');
      } else {
        toast.warning('Utente creato, ma email non inviata. Usa "Reinvia Email" dalla lista utenti.');
      }
      setShowNewUserModal(false);
      setNewUser({
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        role: 'external_technician'
      });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione dell\'utente');
    }
  };

  // Reinvia email di attivazione
  const handleResendActivation = async (userId) => {
    try {
      const response = await fetch(`/api/auth/resend-activation`, {
        method: 'POST',
        headers: {
          withCredentials: true,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: userId })
      });
      
      const data = await response.json();
      if (data.email_sent === true) {
        toast.success('Email di attivazione inviata!');
      } else if (data.activation_token) {
        // Email failed but token generated - show truncated version to admin
        toast.warning(`Email non inviata. Token manuale: ${data.activation_token.substring(0, 20)}...`);
        // Note: Full token visible in API response for admin if needed
      } else {
        toast.error(data.error || 'Errore nell\'invio dell\'email');
      }
    } catch (error) {
      toast.error('Errore nell\'invio dell\'email');
    }
  };

  // Forza password
  const handleForcePassword = async () => {
    if (!selectedUser || forcePassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    
    try {
      const response = await fetch(`/api/auth/force-password`, {
        method: 'POST',
        headers: {
          withCredentials: true,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: selectedUser.id, new_password: forcePassword })
      });
      
      if (!response.ok) throw new Error('Errore');
      
      toast.success('Password aggiornata!');
      setShowForcePasswordModal(false);
      setForcePassword('');
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento della password');
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await userApi.update(userId, { is_active: !currentStatus });
      toast.success('Stato utente aggiornato');
      loadUsers();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  // Change user role (admin only)
  const handleChangeRole = async (userId, newRole) => {
    try {
      await userApi.update(userId, { role: newRole });
      toast.success('Ruolo utente aggiornato');
      loadUsers();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento del ruolo');
    }
  };

  // Delete user (admin only)
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await userApi.delete(userToDelete.id);
      toast.success(`Utente ${userToDelete.email} eliminato`);
      setShowDeleteModal(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'badge-error';
      case 'administration': return 'badge-info';
      case 'project_manager': return 'badge-warning';
      case 'external_technician': return 'badge-success';
      default: return 'badge-neutral';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h2">Utenti</h1>
          <p className="text-body-small mt-1">Gestisci utenti e permessi</p>
        </div>
        {isAdmin && (
          <Button 
            className="btn-primary" 
            onClick={() => setShowNewUserModal(true)}
            data-testid="new-user-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Utente
          </Button>
        )}
      </div>

      {/* Search */}
      <Card className="card-abitalo">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
            <Input
              placeholder="Cerca per nome o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-abitalo pl-10"
              data-testid="users-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="card-abitalo">
        <CardContent className="p-0">
          <div className="table-responsive">
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead className="p-4">Utente</TableHead>
                  <TableHead className="p-4">Contatti</TableHead>
                  <TableHead className="p-4">Ruolo</TableHead>
                  <TableHead className="p-4">Stato</TableHead>
                  <TableHead className="p-4 text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-[#737373]">
                      Nessun utente trovato
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((userData) => (
                    <TableRow key={userData.id} className="table-row" data-testid={`user-row-${userData.id}`}>
                      <TableCell className="table-cell">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#FFF0E5] rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-[#FF7819]">
                              {userData.first_name[0]}{userData.last_name[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-[#3C3C3C]">{userData.first_name} {userData.last_name}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="table-cell">
                        <div className="space-y-1">
                          <p className="flex items-center gap-1 text-sm">
                            <Mail className="w-3 h-3 text-[#737373]" />
                            {userData.email}
                          </p>
                          {userData.phone && (
                            <p className="flex items-center gap-1 text-sm text-[#737373]">
                              <Phone className="w-3 h-3" />
                              {userData.phone}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="table-cell">
                        {isAdmin && userData.role !== 'admin' ? (
                          <Select
                            value={userData.role}
                            onValueChange={(newRole) => handleChangeRole(userData.id, newRole)}
                          >
                            <SelectTrigger className="w-[160px] h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="project_manager">Project Manager</SelectItem>
                              <SelectItem value="external_technician">Tecnico</SelectItem>
                              <SelectItem value="furniture_sales">Arredi</SelectItem>
                              <SelectItem value="administration">Amministrazione</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={getRoleBadgeColor(userData.role)}>
                            {roleLabels[userData.role]}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="table-cell">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${userData.is_active ? 'bg-[#008A3D]' : 'bg-[#CC0000]'}`} />
                            <span className="text-sm text-[#737373]">
                              {userData.is_active ? 'Attivo' : 'Disattivato'}
                            </span>
                          </div>
                          {/* Password status */}
                          {userData.password_set === false && (
                            <div className="flex items-center gap-1 text-xs text-amber-600">
                              <AlertCircle className="w-3 h-3" />
                              Password non impostata
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="table-cell text-right">
                        {isAdmin && (
                          <div className="flex items-center justify-end gap-2">
                            {/* Toggle attivo/disattivo */}
                            {userData.role !== 'admin' && (
                              <Switch
                                checked={userData.is_active}
                                onCheckedChange={() => handleToggleActive(userData.id, userData.is_active)}
                                data-testid={`toggle-user-${userData.id}`}
                              />
                            )}
                            
                            {/* Menu azioni password */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleResendActivation(userData.id)}>
                                  <Send className="w-4 h-4 mr-2" />
                                  {userData.password_set === false ? 'Reinvia Email Attivazione' : 'Reset Password'}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                  setSelectedUser(userData);
                                  setShowForcePasswordModal(true);
                                }}>
                                  <Key className="w-4 h-4 mr-2" />
                                  Forza Nuova Password
                                </DropdownMenuItem>
                                {/* Delete user - only if not current user */}
                                {userData.id !== currentUser?.id && (
                                  <DropdownMenuItem 
                                    onClick={() => {
                                      setUserToDelete(userData);
                                      setShowDeleteModal(true);
                                    }}
                                    className="text-red-600 focus:text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Elimina Utente
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* New User Modal */}
      <Dialog open={showNewUserModal} onOpenChange={setShowNewUserModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-h3">Nuovo Utente</DialogTitle>
            <DialogDescription>
              L'utente riceverà un'email per impostare la propria password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  value={newUser.first_name}
                  onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                  className="input-abitalo"
                  required
                  data-testid="new-user-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Cognome *</Label>
                <Input
                  value={newUser.last_name}
                  onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                  className="input-abitalo"
                  required
                  data-testid="new-user-last-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="input-abitalo"
                required
                data-testid="new-user-email"
              />
              <p className="text-xs text-gray-500">
                Verrà inviato un link di attivazione a questo indirizzo
              </p>
            </div>

            <div className="space-y-2">
              <Label>Telefono</Label>
              <Input
                value={newUser.phone}
                onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                className="input-abitalo"
              />
            </div>

            <div className="space-y-2">
              <Label>Ruolo *</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Amministratore</SelectItem>
                  <SelectItem value="administration">Amministrazione</SelectItem>
                  <SelectItem value="project_manager">Project Manager</SelectItem>
                  <SelectItem value="external_technician">Tecnico Esterno</SelectItem>
                  <SelectItem value="furniture_sales">Venditore Arredi</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewUserModal(false)}>
                Annulla
              </Button>
              <Button type="submit" className="btn-primary" data-testid="submit-new-user">
                <Send className="w-4 h-4 mr-2" />
                Crea e Invia Invito
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Force Password Modal */}
      <Dialog open={showForcePasswordModal} onOpenChange={setShowForcePasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-h3">Forza Nuova Password</DialogTitle>
            <DialogDescription>
              Imposta una nuova password per {selectedUser?.first_name} {selectedUser?.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nuova Password</Label>
              <Input
                type="password"
                value={forcePassword}
                onChange={(e) => setForcePassword(e.target.value)}
                placeholder="Minimo 6 caratteri"
                className="input-abitalo"
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              L'utente potrà accedere immediatamente con questa password. 
              È consigliato comunicargliela in modo sicuro.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowForcePasswordModal(false);
              setForcePassword('');
              setSelectedUser(null);
            }}>
              Annulla
            </Button>
            <Button onClick={handleForcePassword} className="btn-primary">
              <Key className="w-4 h-4 mr-2" />
              Imposta Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={(open) => {
        setShowDeleteModal(open);
        if (!open) setUserToDelete(null);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Elimina Utente
            </DialogTitle>
            <DialogDescription>
              Questa azione è irreversibile. L'utente non potrà più accedere al sistema.
            </DialogDescription>
          </DialogHeader>
          {userToDelete && (
            <div className="py-4">
              <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                <p className="font-medium">{userToDelete.first_name} {userToDelete.last_name}</p>
                <p className="text-sm text-gray-600">{userToDelete.email}</p>
                <p className="text-sm text-gray-500">{roleLabels[userToDelete.role]}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Annulla
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteUser}
              disabled={deleting}
              data-testid="confirm-delete-user-btn"
            >
              {deleting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Eliminazione...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina Utente
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
